/**
 * useOrbDocDownloadsCliENDocBodyLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocDownloadsCliENDocBody
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocDownloadsCliENDocView as OrbDocDownloadsCliENDocViewEntity, OrbDocDownloadsCliENDocBodyEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocDownloadsCliENDocBodyState =
  | 'idle';

export type OrbDocDownloadsCliENDocBodyEvent =
  | 'INIT';

type OrbDocDownloadsCliENDocBodySlot = 'main';
interface OrbDocDownloadsCliENDocBodyInternalState {
  machineState: OrbDocDownloadsCliENDocBodyState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocDownloadsCliENDocBodyEvent | null;
  slotWriter: Partial<Record<OrbDocDownloadsCliENDocBodySlot, OrbDocDownloadsCliENDocBodyEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocDownloadsCliENDocBodyEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocDownloadsCliENDocViewEntity>;
}

type OrbDocDownloadsCliENDocBodyAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocDownloadsCliENDocBodyEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocDownloadsCliENDocBodySlot[]; renderClear?: OrbDocDownloadsCliENDocBodySlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocDownloadsCliENDocBodyEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocDownloadsCliENDocViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocDownloadsCliENDocBodyReducer(
  state: OrbDocDownloadsCliENDocBodyInternalState,
  action: OrbDocDownloadsCliENDocBodyAction
): OrbDocDownloadsCliENDocBodyInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocDownloadsCliENDocBodyState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocDownloadsCliENDocViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocDownloadsCliENDocViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocDownloadsCliENDocViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocDownloadsCliENDocBodyInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { DocNavItem } from '@app/shared';
export interface OrbDocDownloadsCliENDocBodyConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  content: { children: Array<{ children: Array<{ className: string; content: string; type: string; variant: string; weight: string } | { code: string; language: string; maxHeight: string; showCopyButton: boolean; showLineNumbers: boolean; type: string } | { content: string; direction: string; type: string }>; direction: string; gap: string; id: string; type: string } | { children: Array<{ className: string; content: string; type: string; variant: string; weight: string } | { code: string; language: string; maxHeight: string; showCopyButton: boolean; showLineNumbers: boolean; type: string }>; direction: string; gap: string; id: string; type: string } | { content: string; direction: string; type: string }>; direction: string; gap: string; type: string };
  crumbs: Array<{ href: string; label: string } | { label: string }>;
  direction: 'ltr' | 'rtl';
  lede: string;
  navItems: Array<{ items: Array<{ active: boolean; href: string; label: string } | { items: { active: boolean; href: string; label: string }[]; label: string }>; label: string } | { items: { active: boolean; href: string; label: string }[]; label: string }>;
  next: { href: string; label: string };
  prev: { href: string; label: string };
  sidebarLabel: string;
  title: string;
  toc: { id: string; label: string; level: number }[];
  tocLabel: string;
}

const DEFAULT_ORB_DOC_DOWNLOADS_CLI_EN_DOC_BODY_CONFIG = {
  content: { children: [{ content: "`orb` (also installed as `orbital`) is one native binary. Install it from the [installation](/docs/getting-started/installation) page.", direction: "ltr", type: "markdown-content" }, { children: [{ className: "scroll-mt-20", content: "Check", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb validate app.lolo                 # 0 errors and 0 warnings is the bar\norb validate app.lolo --json          # findings with codes and source positions\norb verify app.lolo                   # play every transition in the Rust kernel\norb verify app.lolo --trait Checkout  # one trait at a time\norb verify app.lolo --all             # the Rust kernel, the JS runtime and the compiled app\norb test app.lolo                     # exhaustive state-machine tests", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "check", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Build and run", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb serve app.lolo --open             # compile and serve with the bundled runtime\norb compile app.lolo --output ./out   # write a full TypeScript app\norb compile app.lolo --mode client    # client only: a static site for browser-stored data\norb compile app.lolo --shell python   # other backends: python, rust, mobile, android, ios\norb new my-app                        # scaffold a project", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "build", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Inspect", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb emit orb app.lolo                 # the lowered .orb JSON\norb resolve app.lolo --output app.orb # every import flattened\norb emit ts app.lolo --section Checkout   # generated code for one part\norb execute app.lolo --event PAY --payload '{\"amount\": 40}'\norb format app.lolo                   # pretty-print in place\norb evaluate '(math/clamp 12 0 10)'   # evaluate an expression", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "inspect", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Behaviors and engines", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb list --global                     # installed orb packages, and whether each loads\norb add <package>                     # add one to a project\norb engines install --global          # the verify engines for --runtime / --compiled\norb signatures --out signatures.lolo  # the type catalog agents read", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }], direction: "vertical", gap: "md", id: "packages", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Agent", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb \"Build a booking system for a small clinic\"\norb --last \"Add a cancellation fee\"\norb fix app.lolo", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }, { content: "See [generating programs with AI](/docs/tutorials/advanced/ai-generation).", direction: "ltr", type: "markdown-content" }], direction: "vertical", gap: "md", id: "agent", type: "stack" }, { children: [{ className: "scroll-mt-20", content: "Editor integration", type: "typography", variant: "h2", weight: "semibold" }, { code: "orb complete app.lolo --offset 512    # what may be written at a cursor, as JSON\norb embeds app.lolo                   # where each inline behavior embed sits", language: "bash", maxHeight: "520px", showCopyButton: true, showLineNumbers: false, type: "code-block" }, { content: "Exit codes: `0` means everything requested passed, `1` means a check failed, and `2` means a tool could not run.", direction: "ltr", type: "markdown-content" }], direction: "vertical", gap: "md", id: "editor", type: "stack" }], direction: "vertical", gap: "xl", type: "stack" },
  crumbs: [{ href: "/docs", label: "Documentation" }, { label: "Tools" }, { label: "CLI" }],
  direction: "ltr",
  lede: "Every orb command: validate, verify, compile, serve, and the tools around them.",
  navItems: [{ items: [{ active: false, href: "/docs", label: "Overview" }], label: "Overview" }, { items: [{ active: false, href: "/docs/getting-started/introduction", label: "Introduction" }, { active: false, href: "/docs/getting-started/installation", label: "Installation" }, { active: false, href: "/docs/getting-started/quickstart", label: "Quickstart" }, { active: false, href: "/docs/getting-started/project-structure", label: "Project structure" }], label: "Getting started" }, { items: [{ active: false, href: "/docs/core-concepts/entities", label: "Entities" }, { active: false, href: "/docs/core-concepts/traits", label: "Traits" }, { active: false, href: "/docs/core-concepts/pages", label: "Pages" }, { active: false, href: "/docs/core-concepts/closed-circuit", label: "Closed circuit" }, { active: false, href: "/docs/core-concepts/composition", label: "Composition" }, { active: false, href: "/docs/core-concepts/patterns", label: "UI patterns" }, { active: false, href: "/docs/core-concepts/standard-library", label: "Standard library" }, { active: false, href: "/docs/core-concepts/localization", label: "Localization" }], label: "Core concepts" }, { items: [{ active: false, href: "/docs/tutorials/beginner/complete-orbital", label: "A complete orbital" }, { active: false, href: "/docs/tutorials/beginner/task-manager", label: "A task manager" }, { active: false, href: "/docs/tutorials/intermediate/ui-patterns", label: "UI patterns" }, { active: false, href: "/docs/tutorials/intermediate/guards", label: "Guards" }, { active: false, href: "/docs/tutorials/intermediate/cross-orbital", label: "Cross-orbital events" }, { active: false, href: "/docs/tutorials/advanced/full-app", label: "A full app" }, { active: false, href: "/docs/tutorials/advanced/ai-generation", label: "AI generation" }], label: "Tutorials" }, { items: [{ items: [{ active: false, href: "/docs/reference/operators", label: "Overview" }, { active: false, href: "/docs/reference/operators/core", label: "Core" }, { active: false, href: "/docs/reference/operators/data", label: "Data" }, { active: false, href: "/docs/reference/operators/time-format", label: "Time and format" }, { active: false, href: "/docs/reference/operators/validate", label: "Validation" }, { active: false, href: "/docs/reference/operators/flow", label: "Flow" }, { active: false, href: "/docs/reference/operators/random", label: "Randomness" }, { active: false, href: "/docs/reference/operators/spatial", label: "Spatial" }, { active: false, href: "/docs/reference/operators/ml", label: "Machine learning" }, { active: false, href: "/docs/reference/operators/host", label: "Host" }], label: "Operators" }, { items: [{ active: false, href: "/docs/reference/behaviors", label: "Overview" }, { active: false, href: "/docs/reference/behaviors/ui-core", label: "UI core" }, { active: false, href: "/docs/reference/behaviors/ui-game", label: "Games" }, { active: false, href: "/docs/reference/behaviors/ui-learning", label: "Learning" }, { active: false, href: "/docs/reference/behaviors/ui-avl", label: "AVL" }, { active: false, href: "/docs/reference/behaviors/agent", label: "Agents" }, { active: false, href: "/docs/reference/behaviors/infra", label: "Infrastructure" }, { active: false, href: "/docs/reference/behaviors/ml", label: "Machine learning" }], label: "Behaviors" }, { active: false, href: "/docs/reference/avl", label: "AVL" }, { active: false, href: "/docs/reference/validation", label: "Validation" }], label: "Reference" }, { items: [{ active: true, href: "/docs/downloads/cli", label: "CLI" }, { active: false, href: "/docs/downloads/skills", label: "Agent skills" }], label: "Tools" }, { items: [{ active: false, href: "/docs/community/contributing", label: "Contributing" }, { active: false, href: "/docs/enterprise/index", label: "For teams" }], label: "Community" }],
  next: { href: "/docs/downloads/skills", label: "Agent skills" },
  prev: { href: "/docs/reference/validation", label: "Validation" },
  sidebarLabel: "Documentation",
  title: "The orb CLI",
  toc: [{ id: "check", label: "Check", level: 2 }, { id: "build", label: "Build and run", level: 2 }, { id: "inspect", label: "Inspect", level: 2 }, { id: "packages", label: "Behaviors and engines", level: 2 }, { id: "agent", label: "Agent", level: 2 }, { id: "editor", label: "Editor integration", level: 2 }],
  tocLabel: "On this page",
} as OrbDocDownloadsCliENDocBodyConfig;

export interface OrbDocDownloadsCliENDocBodyLogicReturn {
  state: OrbDocDownloadsCliENDocBodyState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocDownloadsCliENDocBodyEvent | null;
  slotWriter: Partial<Record<OrbDocDownloadsCliENDocBodySlot, OrbDocDownloadsCliENDocBodyEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocDownloadsCliENDocBodyEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocDownloadsCliENDocViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocDownloadsCliENDocBodyEventPayloadMap]: (payload?: OrbDocDownloadsCliENDocBodyEventPayloadMap[K]) => Promise<EventResponse> };
  config: OrbDocDownloadsCliENDocBodyConfig;
}

export function useOrbDocDownloadsCliENDocBodyLogic(config?: Partial<OrbDocDownloadsCliENDocBodyConfig>): OrbDocDownloadsCliENDocBodyLogicReturn {
  const mergedConfig: OrbDocDownloadsCliENDocBodyConfig = { ...DEFAULT_ORB_DOC_DOWNLOADS_CLI_EN_DOC_BODY_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-downloads-cli-en-doc-body', orbitalName: 'OrbDocDownloadsCliEN', traitName: 'OrbDocDownloadsCliENDocBody' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocDownloadsCliENDocBodyReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocDownloadsCliENDocView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocDownloadsCliENDocBodySlot[]; clear: OrbDocDownloadsCliENDocBodySlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocDownloadsCliENDocBodySlot[], clear: [] as OrbDocDownloadsCliENDocBodySlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocDownloadsCliENDocView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocDownloadsCliENDocBodyEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocDownloadsCliENDocBody': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocDownloadsCliENDocBodyEventPayloadMap]: (payload?: OrbDocDownloadsCliENDocBodyEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
  };

  useUIEvents(enqueueEvent, 'OrbDocDownloadsCliEN.OrbDocDownloadsCliENDocBody', ['INIT'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocDownloadsCliENDocBody', (): TraitStateSnapshot => ({
      traitName: 'OrbDocDownloadsCliENDocBody',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
